import { defineStore } from "pinia";
import { ref } from "vue";
import { useUIStore } from "./ui";
import apiService from "../services/apiService";
import logService from "../services/logService";
import axiosInstance from "../utils/axios";

export const useServiceStore = defineStore("services", () => {
  const uiStore = useUIStore();

  // State
  const services = ref([]);
  const categories = ref([]);
  const packages = ref([]);
  const currentService = ref(null);
  const loading = ref(false);

  // API Functions
  const fetchServices = async (params = {}) => {
    try {
      loading.value = true;
      logService.info('Fetching services...', params);
      const response = await apiService.get('/services', params);
      if (response.success) {
        services.value = response.data;
        return response.data;
      }
      return [];
    } catch (error) {
      logService.error('Failed to fetch services:', error);
      return [];
    } finally {
      loading.value = false;
    }
  };

  const fetchAdminServices = async () => {
    try {
      loading.value = true;
      logService.info('Fetching admin services...');
      const response = await apiService.get('/admin/services');
      if (response.success) {
        services.value = response.data;
      }
    } catch (error) {
      logService.error("Failed to fetch admin services:", error);
    } finally {
      loading.value = false;
    }
  };

  const addService = async (serviceData) => {
    try {
      loading.value = true;
      logService.info('Adding service...', serviceData);
      const response = await apiService.post('/admin/services', serviceData);
      if (response.success) {
        services.value.push(response.data);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true, data: response.data };
      }
    } catch (error) {
      logService.error("Failed to add service:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const updateService = async (id, serviceData) => {
    try {
      loading.value = true;
      logService.info(`Updating service: ${id}`, serviceData);
      const response = await apiService.put(`/admin/services/${id}`, serviceData);
      if (response.success) {
        const index = services.value.findIndex((s) => s.id === id);
        if (index !== -1) {
          services.value[index] = response.data;
        }
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true, data: response.data };
      }
    } catch (error) {
      logService.error("Failed to update service:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const deleteService = async (id) => {
    try {
      loading.value = true;
      logService.info(`Deleting service: ${id}`);
      const response = await apiService.delete(`/admin/services/${id}`);
      if (response.success) {
        services.value = services.value.filter((s) => s.id !== id);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true };
      }
    } catch (error) {
      logService.error("Failed to delete service:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const bulkDeleteServices = async (ids) => {
    try {
      loading.value = true;
      logService.info('Bulk deleting services...', ids);
      const response = await apiService.post('/admin/services/bulk-delete', { ids });
      if (response.success) {
        services.value = services.value.filter((s) => !ids.includes(s.id));
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true };
      }
    } catch (error) {
      logService.error("Failed to bulk delete services:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const bulkUpdateStatus = async (ids, status) => {
    try {
      loading.value = true;
      logService.info(`Bulk updating status to ${status}...`, ids);
      const response = await apiService.post('/admin/services/bulk-status', { ids, status });
      if (response.success) {
        services.value = services.value.map((s) =>
          ids.includes(s.id) ? { ...s, status } : s
        );
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true };
      }
    } catch (error) {
      logService.error("Failed to bulk update status:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const duplicateService = async (id) => {
    try {
      loading.value = true;
      logService.info(`Duplicating service: ${id}`);
      const response = await apiService.post(`/admin/services/${id}/duplicate`);
      if (response.success) {
        services.value.push(response.data);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return response.data;
      }
    } finally {
      loading.value = false;
    }
  };

  const togglePublicStatus = async (id) => {
    try {
      loading.value = true;
      logService.info(`Toggling public status for service: ${id}`);
      const response = await apiService.post(`/admin/services/${id}/toggle-public-status`);
      if (response.success) {
        const index = services.value.findIndex((s) => s.id === id);
        if (index !== -1) {
          services.value[index].public_status = response.data.public_status;
        }
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return { success: true, data: response.data };
      }
    } catch (error) {
      logService.error("Failed to toggle public status:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const fetchServiceById = async (id) => {
    try {
      logService.info(`Fetching service by ID: ${id}`);
      const response = await apiService.get(`/services/${id}`);
      if (response.success) {
        currentService.value = response.data;
        return response.data;
      }
      return null;
    } catch (error) {
      logService.error("Failed to fetch service by id:", error);
      return null;
    }
  };

  const fetchCategories = async () => {
    try {
      logService.info('Fetching service categories...');
      const response = await apiService.get('/service-categories');
      if (response.success) {
        categories.value = response.data;
        return response.data;
      }
      return [];
    } catch (error) {
      logService.error("Failed to fetch categories:", error);
      return [];
    }
  };

  const fetchAdminCategories = async () => {
    try {
      logService.info('Fetching admin service categories...');
      const response = await apiService.get('/admin/service-categories');
      if (response.success) {
        categories.value = response.data;
        return response.data;
      }
      return [];
    } catch (error) {
      logService.error("Failed to fetch admin categories:", error);
      return [];
    }
  };

  const addCategory = async (categoryData) => {
    try {
      logService.info('Adding service category...', categoryData);
      const response = await apiService.post('/service-categories', categoryData);
      if (response.success) {
        categories.value.push(response.data);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
      }
    } catch (error) {
      logService.error("Failed to add category:", error);
    }
  };

  const updateCategory = async (id, categoryData) => {
    try {
      logService.info(`Updating service category: ${id}`, categoryData);
      const response = await apiService.put(`/service-categories/${id}`, categoryData);
      if (response.success) {
        const index = categories.value.findIndex((c) => c.id === id);
        if (index !== -1) categories.value[index] = response.data;
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
      }
    } catch (error) {
      logService.error("Failed to update category:", error);
    }
  };

  const deleteCategory = async (id) => {
    try {
      logService.info(`Deleting service category: ${id}`);
      const response = await apiService.delete(`/service-categories/${id}`);
      if (response.success) {
        categories.value = categories.value.filter((c) => c.id !== id);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
      }
    } catch (error) {
      logService.error("Failed to delete category:", error);
    }
  };

  const fetchServicePackages = async (serviceId) => {
    try {
      logService.info(`Fetching packages for service: ${serviceId}`);
      const response = await apiService.get(`/services/${serviceId}/packages`);
      if (response.success) {
        return response.data;
      }
      return [];
    } catch (error) {
      logService.error("Failed to fetch service packages:", error);
      return [];
    }
  };

  const fetchPackages = async (serviceId = null) => {
    try {
      logService.info('Fetching packages...', { serviceId });
      const params = serviceId ? { service_id: serviceId } : {};
      const response = await apiService.get('/service-packages', params);
      if (response.success) {
        packages.value = response.data;
        return response.data;
      }
      return [];
    } catch (error) {
      logService.error("Failed to fetch packages:", error);
      return [];
    }
  };

  const addPackage = async (packageData) => {
    try {
      logService.info('Adding service package...', packageData);
      const response = await apiService.post('/service-packages', packageData);
      if (response.success) {
        packages.value.push(response.data);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: "Package created successfully",
        });
        return response.data;
      }
    } catch (error) {
      logService.error("Failed to add package:", error);
      throw error;
    }
  };

  const updatePackage = async (id, packageData) => {
    try {
      logService.info(`Updating service package: ${id}`, packageData);
      const response = await apiService.put(`/service-packages/${id}`, packageData);
      if (response.success) {
        const index = packages.value.findIndex((p) => p.id === id);
        if (index !== -1) packages.value[index] = response.data;
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: "Package updated successfully",
        });
        return response.data;
      }
    } catch (error) {
      logService.error("Failed to update package:", error);
      throw error;
    }
  };

  const deletePackage = async (id) => {
    try {
      logService.info(`Deleting service package: ${id}`);
      const response = await apiService.delete(`/service-packages/${id}`);
      if (response.success) {
        packages.value = packages.value.filter((p) => p.id !== id);
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: "Package deleted successfully",
        });
      }
    } catch (error) {
      logService.error("Failed to delete package:", error);
      throw error;
    }
  };

  const uploadServiceBanner = async (serviceId, file) => {
    try {
      loading.value = true;
      logService.info(`Uploading banner for service: ${serviceId}`);
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiService.upload(`/admin/services/${serviceId}/upload-banner`, formData);

      if (response.success) {
        const index = services.value.findIndex((s) => s.id === serviceId);
        if (index !== -1) {
          services.value[index].image = response.data.image;
        }
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
        return response.data;
      }
    } catch (error) {
      logService.error("Failed to upload banner:", error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const deleteServiceBanner = async (serviceId) => {
    try {
      loading.value = true;
      logService.info(`Deleting banner for service: ${serviceId}`);
      const response = await apiService.delete(`/admin/services/${serviceId}/delete-banner`);
      if (response.success) {
        const index = services.value.findIndex((s) => s.id === serviceId);
        if (index !== -1) {
          services.value[index].image = null;
        }
        uiStore.showNotification({
          type: "success",
          title: "Success",
          message: response.message,
        });
      }
    } catch (error) {
      logService.error("Failed to delete banner:", error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const getBannerUrl = (imagePath) => {
    if (!imagePath) return null;
    const baseURL = axiosInstance.defaults.baseURL || "http://localhost:8000/api/v1";
    const cleanBaseURL = baseURL.replace("/api/v1", "");
    return `${cleanBaseURL}/storage/${imagePath}`;
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    // If it's already a full URL, return as is
    if (iconPath.startsWith('http')) return iconPath;
    const baseURL = axiosInstance.defaults.baseURL || "http://localhost:8000/api/v1";
    const cleanBaseURL = baseURL.replace("/api/v1", "");
    return `${cleanBaseURL}/storage/${iconPath}`;
  };

  return {
    services,
    categories,
    packages,
    currentService,
    loading,
    fetchServices,
    fetchAdminServices,
    fetchServiceById,
    fetchCategories,
    fetchServicePackages,
    fetchPackages,
    addService,
    updateService,
    deleteService,
    addCategory,
    updateCategory,
    deleteCategory,
    addPackage,
    updatePackage,
    deletePackage,
    bulkDeleteServices,
    bulkUpdateStatus,
    duplicateService,
    fetchAdminCategories,
    uploadServiceBanner,
    deleteServiceBanner,
    togglePublicStatus,
    getBannerUrl,
    getIconUrl,
    getServiceById: (id) => services.value.find((s) => s.id === id),
    getServicesByCategory: (categoryId) => services.value.filter((s) => s.categories && s.categories.some(c => c.id === categoryId)),
    getFeaturedServices: () => services.value.filter((s) => s.featured && s.status === "active"),
    getActiveServices: () => services.value.filter((s) => s.status === "active"),
    getCategoryById: (id) => categories.value.find((c) => c.id === id),
  };
});
